/**
 * The planning flow's state (#163): Planlegg opens the menu, «Få et planforslag» reads three fills of
 * the week's open shifts, one is chosen and drawn on the grid, «Se endringer» lists it, «Lagre som
 * utkast» saves it. Only the save writes; it carries one request key from the moment a proposal is
 * shown, so a retry after a lost answer replays rather than writes twice.
 */
import { ApiError } from '@/api/client';
import type { ApiShift } from '@/api/shifts';
import { T, useT } from '@/i18n';
import { type Attached, openHugin, useAttached, useProposalAsk } from '@/poc-chat/attach';
import { isoWeek as isoWeekOf } from '@timefly/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { newKey } from '../copyMachine';
import {
  type Ack,
  type BasisRef,
  type Edit,
  type Lift,
  type Preview,
  type Proposal,
  applyFill,
  previewFill,
  reviseFill,
} from './api';
import { type Option, assess, kr, marksOf, optionsOf, overlay } from './model';

export type Step = 'menu' | 'basis' | 'options' | 'chosen' | 'changes' | 'edit';
/** The shift being edited inside the proposal: where it is and what it is now. */
export interface Editing {
  target: Edit['target'];
  user_id: number | null;
  start_time: string;
  end_time: string;
}
export interface Saved {
  n: number;
  /** of those, open shifts that were already published and stay so */
  publishedOpen: number;
}

export function usePlanning<U extends { id: number }>({
  week,
  teamId,
  shifts,
  users,
  onSaved,
  company = '',
}: {
  /** the company's name, for the chat's context line */
  company?: string;
  week: string;
  teamId: number | null;
  /** the saved shifts as read */
  shifts: ApiShift[];
  users: U[];
  onSaved: () => void;
}) {
  const say = useT();
  const [step, setStep] = useState<Step>('menu');
  const [data, setData] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [shownId, setShownId] = useState<string | null>(null);
  const [side, setSide] = useState<'before' | 'after'>('after');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Saved | null>(null);
  // one key per exact request: a retry of the same body reuses it, any other body gets a new one
  const [attempt, setAttempt] = useState<{ key: string; sig: string } | null>(null);
  // P08: the manager's edits on top of the chosen candidate, and the version they make
  const [edits, setEdits] = useState<Edit[]>([]);
  const [edited, setEdited] = useState<Proposal | null>(null);
  const [version, setVersion] = useState(1);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [revising, setRevising] = useState(false);
  // revision 6: acknowledgements by finding id, each bound to the fingerprint it was given for
  const [acks, setAcks] = useState<Record<string, Ack>>({});
  // the side panel's two tabs on Vaktplan (owner 18.10): Planlegg, and Hugin docked
  const [tab, setTab] = useState<'plan' | 'hugin'>('plan');
  // the version number as of now, for an answer that must name it before React re-renders
  const versionNow = useRef(1);
  const setVer = (v: number) => {
    versionNow.current = v;
    setVersion(v);
  };
  // revision 5: an empty week is planned from a basis the manager picks and confirms
  const [basisPick, setBasisPick] = useState<string | null>(null);
  const [basis, setBasis] = useState<BasisRef | null>(null);
  // the limits the manager lifted after seeing what stopped the plan (owner 18.10)
  const [lift, setLift] = useState<Lift[]>([]);
  const [bases, setBases] = useState<Preview['bases'] | null>(null);

  const options: Option[] = useMemo(() => (data ? optionsOf(data, say) : []), [data, say]);
  const shown = options.find((o) => o.id === shownId) ?? null;
  const reset = () => {
    setStep('menu');
    setData(null);
    setError('');
    setPicked(null);
    setShownId(null);
    setSide('after');
    setEdits([]);
    setEdited(null);
    setVer(1);
    setEditing(null);
    setAcks({});
    setBasis(null);
    setLift([]);
    setBasisPick(null);
    setBases(null);
  };
  // another week, another plan
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only on a new week or team
  useEffect(() => {
    reset();
    setSaved(null);
  }, [week, teamId]);

  const suggest = async (on: BasisRef | null = basis, lifted: Lift[] = lift) => {
    setStep('options');
    setLoading(true);
    setError('');
    try {
      const d = await previewFill(week, teamId, on, lifted);
      if (d.basis_required) {
        setData(null);
        setBases(d.bases ?? null);
        const first = d.bases?.templates[0];
        // a template first, as before, then the recurring pattern, then the week before as it was (owner 18.10)
        setBasisPick(
          d.bases?.need?.slots
            ? 'need'
            : first
              ? `template:${first.template_id}`
              : d.bases?.history.slots
                ? 'history'
                : d.bases?.week?.slots
                  ? 'week'
                  : null,
        );
        setStep('basis');
        return;
      }
      setData(d);
      // the first option is on the grid at once (owner 18.10: choosing should already show the draft)
      const first = d.proposals[0]?.origin.candidate_id ?? null;
      setPicked((p) => p ?? first);
      setShownId((x) => x ?? first);
      setSide('after');
    } catch (e) {
      setData(null);
      setError(
        e instanceof ApiError && e.status === 404
          ? 'Planforslag er ikke slått på for denne bedriften ennå.'
          : e instanceof ApiError && e.status >= 400 && e.status < 500
            ? e.message
            : 'Forslaget kunne ikke hentes. Prøv igjen.',
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * A week plan from the chat (plan_week): the panel's own «Få et planforslag» on this basis, as if
   * «Bruk dette grunnlaget» had been pressed; the three are drawn and the first picked. Nothing is saved.
   */
  const openProposal = (b: BasisRef | null, l: Lift[] = []) => {
    reset();
    setSaved(null);
    setBasis(b);
    setLift(l);
    void suggest(b, l);
  };
  // the chat asked for a plan of this week: opened once the grid is on it (Vaktplan moves the week)
  const asked = useProposalAsk((s) => s.asked);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new ask or the week reaching it is the trigger
  useEffect(() => {
    if (!asked || asked.week_start !== week) return;
    useProposalAsk.getState().clear();
    openProposal(asked.basis, asked.lift ?? []);
  }, [asked, week]);

  /** «Bruk dette grunnlaget»: the picked basis becomes the week's demand, and the three are read */
  const confirmBasis = () => {
    if (!basisPick) return;
    const b: BasisRef =
      basisPick === 'history'
        ? { kind: 'history' }
        : basisPick === 'week'
          ? { kind: 'week' }
          : basisPick === 'need'
            ? { kind: 'need' }
            : { kind: 'template', template_id: Number(basisPick.split(':')[1]) };
    setBasis(b);
    setPicked(null);
    void suggest(b);
  };

  /** Choosing an option draws it on the grid at once; nothing is saved (owner 18.10). */
  const pick = (id: string | null) => {
    setPicked(id);
    if (id !== shownId) {
      setEdits([]);
      setEdited(null);
      setVer(1);
      setAcks({});
    }
    setShownId(id);
    setSide('after');
    setSaved(null);
  };

  const show = () => {
    if (!picked) return;
    if (picked !== shownId) {
      setEdits([]);
      setEdited(null);
      setVer(1);
      setAcks({});
    }
    setShownId(picked);
    setSide('after');
    setSaved(null);
    setStep('chosen');
  };

  const proposal = edited ?? shown?.proposal ?? null;
  const assessment = useMemo(
    () => (proposal && data ? assess(proposal, data, acks, say) : []),
    [proposal, data, acks, say],
  );

  const save = async () => {
    if (!shown || !proposal || saving) return false;
    setSaving(true);
    setError('');
    const held = proposal.findings.items
      .filter((f) => acks[f.finding_id]?.fingerprint === f.fingerprint)
      .map((f) => acks[f.finding_id] as Ack);
    const body = {
      week_start: week,
      team_id: teamId,
      candidate_id: shown.id,
      proposal_revision: proposal.revision,
      ...(basis ? { basis } : {}),
      ...(lift.length ? { lift } : {}),
      ...(edits.length ? { edits } : {}),
      ...(held.length ? { acknowledgements: held } : {}),
    };
    const sig = JSON.stringify(body);
    const key = attempt?.sig === sig ? attempt.key : newKey();
    setAttempt({ key, sig });
    try {
      const r = await applyFill({ ...body, request_key: key });
      const base = new Map((data?.baseline.shifts ?? []).map((s) => [s.shift_id, s]));
      setSaved({
        n: r.applied.length,
        publishedOpen: r.applied.filter((a) => base.get(a.shift_id)?.published).length,
      });
      reset();
      setAttempt(null);
      onSaved();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'stale') setError(e.message);
      else if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 499)
        setError(e.message);
      // no answer: the key stays, so trying again either finds the first save or makes it once
      else setError('Vi fikk ikke svar. Prøv igjen; forslaget lagres ikke to ganger.');
    } finally {
      setSaving(false);
    }
    return false;
  };

  /** a proposed shift opens the editor; `displayId` is the id the grid draws (a new one is negative) */
  const beginEdit = (displayId: number) => {
    if (!proposal) return;
    const op =
      displayId > 0
        ? proposal.operations.find((o) => o.target.shift_id === displayId)
        : proposal.operations[-displayId - 1];
    if (!op?.after) return;
    setEditing({
      target: op.target,
      user_id: op.after.user_id,
      start_time: op.after.start_time,
      end_time: op.after.end_time,
    });
    setError('');
    setStep('edit');
  };
  /** A set of edits (the manager's, or Hugin's from the chat) made into the next version by the server. */
  const revise = async (next: Edit[], cand: Option | null = shown) => {
    if (!cand) return false;
    setRevising(true);
    setError('');
    try {
      const r = await reviseFill({
        week_start: week,
        team_id: teamId,
        candidate_id: cand.id,
        proposal_revision: cand.revision,
        edits: next,
        ...(basis ? { basis } : {}),
        ...(lift.length ? { lift } : {}),
      });
      setEdits(next);
      setEdited(r.proposal);
      const v = versionNow.current + 1;
      setVer(v);
      return v;
    } catch (e) {
      setError(
        e instanceof ApiError && e.status >= 400 && e.status < 500
          ? e.message
          : 'Endringen kunne ikke regnes ut. Prøv igjen.',
      );
      return 0;
    } finally {
      setRevising(false);
    }
  };
  /** «Oppdater forslaget»: the edits so far plus this one, evaluated by the server as a new version */
  const submitEdit = async (change: Omit<Editing, 'target'>) => {
    if (!shown || !editing || revising) return;
    const same = (t: Edit['target']) =>
      t.shift_id != null ? t.shift_id === editing.target.shift_id : t.client_id === editing.target.client_id;
    if (await revise([...edits.filter((e) => !same(e.target)), { target: editing.target, ...change }])) {
      setEditing(null);
      setStep('chosen');
    }
  };
  const acknowledge = (ids: string[], reason: string) => {
    if (!proposal) return;
    const next = { ...acks };
    for (const f of proposal.findings.items)
      if (ids.includes(f.finding_id))
        next[f.finding_id] = {
          finding_id: f.finding_id,
          fingerprint: f.fingerprint,
          reason: reason.trim() || null,
        };
    setAcks(next);
  };
  const reopen = (ids: string[]) =>
    setAcks(Object.fromEntries(Object.entries(acks).filter(([k]) => !ids.includes(k))));

  /** What the chat shows and sends while this proposal is attached. */
  const attachedOf = (cand: Option, p: Proposal | null, v: number, e: Edit[]): Attached => ({
    context: {
      week_start: week,
      team_id: teamId,
      candidate_id: cand.id,
      proposal_revision: cand.revision,
      edits: e,
      ...(basis ? { basis } : {}),
      ...(lift.length ? { lift } : {}),
    },
    week: isoWeekOf(week),
    company,
    line: `${T('uke {n}', { n: isoWeekOf(week) })}, ${company}`,
    basis: data?.basis
      ? data.basis.kind === 'template'
        ? `${T('vaktmalen')} ${data.basis.ref.name ?? ''}`.trim()
        : T('tidligere uker')
      : null,
    title: T('Forslag: {name}', { name: cand.label }),
    version: v,
    facts: [
      p?.cost.visibility === 'visible' && p.cost.total_minor != null ? kr(p.cost.total_minor) : null,
      p ? T('{h} timer', { h: (p.cost.hours ?? 0).toLocaleString('nb-NO') }) : null,
    ]
      .filter(Boolean)
      .join(', '),
  });
  const attachTo = (cand: Option, e: Edit[] = edits) =>
    useAttached
      .getState()
      .attach(attachedOf(cand, edited ?? cand.proposal, versionNow.current, e), async (next) => {
        const v = await revise(next as Edit[], cand);
        return v || null;
      });
  // the attached copy follows every new version, so the next message carries the edits so far
  // biome-ignore lint/correctness/useExhaustiveDependencies: the version, its edits and its proposal are the triggers
  useEffect(() => {
    const a = useAttached.getState().attached;
    if (!a || !shown || a.context.candidate_id !== shown.id) return;
    useAttached.getState().update(attachedOf(shown, proposal, version, edits));
  }, [proposal, version, edits, shown]);
  // the chat's save button saves what the panel would (owner 18.10); the click is the manager's
  // biome-ignore lint/correctness/useExhaustiveDependencies: save reads the current proposal itself
  useEffect(() => {
    useAttached.setState({
      onSave: shown ? () => save() : null,
    });
  }, [shown, proposal]);
  // the proposal on the grid is always the one Hugin talks about (owner 18.10), not only after «Juster med Hugin»
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new shown option is the trigger
  useEffect(() => {
    if (!shown) return;
    const a = useAttached.getState().attached;
    if (!a || a.context.candidate_id !== shown.id) attachTo(shown);
  }, [shown?.id]);
  // closing the proposal (Forkast, saved, another week, leaving the page) detaches it from the chat
  useEffect(() => {
    if (!shown) useAttached.getState().detach();
  }, [shown]);
  useEffect(() => () => useAttached.getState().detach(), []);

  const visible = side === 'after' ? proposal : null;
  const marks = useMemo(() => marksOf(visible), [visible]);
  const drawn = useMemo(() => overlay(shifts, visible, users), [shifts, visible, users]);
  return {
    step,
    setStep,
    data,
    loading,
    error,
    options,
    picked,
    pick,
    shown,
    side,
    setSide,
    saving,
    saved,
    dismissSaved: () => setSaved(null),
    suggest: () => suggest(),
    show,
    bases,
    basis,
    lift,
    /** «Fortsett likevel»: the same basis planned again with these limits lifted; edits start over */
    liftLimits: (l: Lift[]) => {
      setLift(l);
      setEdits([]);
      setEdited(null);
      setVer(1);
      setAcks({});
      void suggest(basis, l);
    },
    basisPick,
    pickBasis: setBasisPick,
    confirmBasis,
    save,
    discard: reset,
    open: () => {
      if (!shown) setStep('menu');
    },
    /** the shifts the grid draws: the chosen proposal over the saved ones while «Etter» is on */
    shifts: drawn,
    marks,
    /** the version on screen: the candidate, or the latest edit of it */
    proposal,
    version,
    editing,
    revising,
    beginEdit,
    submitEdit,
    cancelEdit: () => {
      setEditing(null);
      setStep('chosen');
    },
    assessment,
    acknowledge,
    reopen,
    tab,
    setTab,
    openProposal,
    /** Hugin's edits from the chat (adjust_plan_proposal), made into the next version by the same path */
    applyChatEdits: (next: Edit[]) => revise(next),
    /** «Juster med Hugin» under a shown proposal: attach it to the floating chat and open it */
    adjustWithHugin: () => {
      if (!shown) return;
      attachTo(shown);
      openHugin();
    },
    /**
     * «Prøv denne føringen» in the comparison: the picked alternative is shown and attached, and the
     * instruction is sent in the floating chat; Hugin's edits come back as its next version.
     */
    tryInstruction: (text: string) => {
      const cand = options.find((o) => o.id === picked) ?? null;
      if (!cand) return false;
      if (cand.id !== shownId) {
        setEdits([]);
        setEdited(null);
        setVer(1);
        setAcks({});
      }
      setShownId(cand.id);
      setSide('after');
      setSaved(null);
      attachTo(cand, edits.length && cand.id === shownId ? edits : []);
      openHugin(text);
      return true;
    },
  };
}

export type Planning = ReturnType<typeof usePlanning>;
